import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const DigitallyOrchestratingCellGeneTherapy: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.executiveSummary" },
    { id: "market-context", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.marketContext" },
    { id: "operational-gaps", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.operationalGaps" },
    { id: "cellcx-operational", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.cellcxOperational" },
    { id: "workflow-digitization", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.workflowDigitization" },
    { id: "strategic-benefits", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.strategicBenefits" },
    { id: "references", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.references" },
    { id: "about", labelKey: "DigitallyOrchestratingCellGeneTherapy.sections.about" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper / Cell Therapy Focus
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            Digitally Orchestrating Cell & Gene Therapy: Why CellCX Provides the Operational Layer Modern EHR Systems Cannot
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Cell and gene therapy (CGT) is rapidly transforming from a niche offering into a core service line for leading health systems and academic medical centers. CAR-T, TIL, NK cell therapies, and emerging allogeneic and off‑the‑shelf products bring extraordinary clinical promise—but also extraordinary operational complexity.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX | Collaborative Healthcare Platform" />
            <MetaItem label="Published" value="December 4, 2025" />
            <MetaItem label="Length" value="18 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "Digitally Orchestrating Cell & Gene Therapy", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "market-context" && "📊 1. Market Context: A Decade of Unprecedented CGT Expansion"}
                {s.id === "operational-gaps" && "🔍 2. Operational Gaps in Major EHR Platforms"}
                {s.id === "cellcx-operational" && "⚙️ 3. CellCX: The Operational Engine that Complements the EHR"}
                {s.id === "workflow-digitization" && "🔄 4. End‑to‑End Workflow Digitization with CellCX"}
                {s.id === "strategic-benefits" && "💡 5. Strategic Benefits of CellCX for CGT Programs"}
                {s.id === "references" && "📚 6. References"}
                {s.id === "about" && "ℹ️ About Eceptionist and CellCX"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Cell and gene therapy (CGT) is rapidly transforming from a niche offering into a core service line for leading health systems and academic medical centers. CAR-T, TIL, NK cell therapies, and emerging allogeneic and off‑the‑shelf products bring extraordinary clinical promise—but also extraordinary operational complexity.
                    </p>
                    <br />
                    <p>
                      Electronic Health Records (EHRs) such as Epic, Oracle Cerner, MEDITECH, and Allscripts/Altera remain essential as clinical systems of record. They are the backbone for documentation, orders, medications, labs, and billing. However, EHRs were never designed to orchestrate the multi‑step, multi‑team, logistics‑intensive workflows required for CGT. They do not coordinate manufacturing timelines, shipping windows, multi‑resource capacity, chain‑of‑identity and chain‑of‑custody (CoI/CoC) workflows, or protocol‑driven readiness gates.
                    </p>
                    <br />
                    <p>
                      CellCX—part of the EceptionistCX platform—fills this critical gap. CellCX is a configurable, cloud‑based workflow engine designed specifically for cell and gene therapy operations. It digitizes and coordinates the entire therapy lifecycle, from referral and eligibility through apheresis, manufacturing, shipping, infusion readiness, and long‑term follow‑up, while integrating bi‑directionally with the EHR. The EHR remains the clinical source of truth. CellCX becomes the operational command center.
                    </p>
                  </>
                )}
                {s.id === "market-context" && (
                  <>
                    <p>
                      The cell and gene therapy landscape is expanding along multiple dimensions: market size, pipeline volume, product diversity, and operational responsibility.
                    </p>
                    <br />
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Market growth</strong> – Industry reports such as IQVIA (2023) estimate that the global CGT market was approximately $20–25 billion in 2023, with annual growth rates in the high‑20% range projected through 2030. Grand View Research similarly forecasts strong double‑digit growth across cell and gene therapy segments.</li>
                      <li><strong>Pipeline volume</strong> – There are now more than 2,000 active CGT clinical trials worldwide, with dozens of late‑stage products approaching commercialization.</li>
                      <li><strong>Product diversity</strong> – Beyond autologous CAR‑T, programs increasingly include TIL therapies, engineered NK cells, gene‑edited products, and rapidly emerging allogeneic/off‑the‑shelf models.</li>
                      <li><strong>Operational responsibility</strong> – Hospitals and academic centers must coordinate not only clinical care, but also insurance and financial clearance, vendor onboarding, scheduling, manufacturing logistics, product storage, and long‑term safety follow‑up.</li>
                    </ul>
                    <br />
                    <p>
                      Each CGT patient can trigger 30–70 distinct operational steps and checkpoints across multiple teams and organizations. At current and projected volumes, a patchwork of spreadsheets, emails, shared drives, and ad‑hoc trackers is no longer sustainable or compliant.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <img 
                        src="/Image/WhitePapers/Digitally Orchestrating Cell & Gene Therapy/Digitally Orchestrating Cell & Gene Therapy_Image1.png" 
                        alt="Market Context: A Decade of Unprecedented CGT Expansion"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                  </>
                )}
                {s.id === "operational-gaps" && (
                  <>
                    <p>
                      EHR platforms are indispensable, but they were designed primarily around clinical encounters—not complex operational orchestration. While each vendor offers configurable workflows, order sets, and limited automations, none provide the end‑to‑end operational infrastructure required to run a modern CGT program. Key gaps are summarized below.
                    </p>
                    <br />
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse border border-slate-300 text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="border border-slate-300 px-4 py-2 text-left font-semibold">Operational Need</th>
                            <th className="border border-slate-300 px-4 py-2 text-left font-semibold">CGT Program Requirements</th>
                            <th className="border border-slate-300 px-4 py-2 text-left font-semibold">What EHRs Provide</th>
                            <th className="border border-slate-300 px-4 py-2 text-left font-semibold">Gap / Consequence</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Multi-Step Workflow Orchestration</strong></td>
                            <td className="border border-slate-300 px-4 py-2">20–70 sequential and conditional workflow steps; multi-team dependencies; cross-functional routing</td>
                            <td className="border border-slate-300 px-4 py-2">Basic tasks and order-linked clinical workflows</td>
                            <td className="border border-slate-300 px-4 py-2">Reliance on spreadsheets and ad-hoc trackers; higher risk of missed steps</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Cross-Team Visibility</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Unified, real-time view across oncology, apheresis, lab, pharmacy, ICU, finance, and vendors</td>
                            <td className="border border-slate-300 px-4 py-2">Department-specific views; no shared operational dashboard</td>
                            <td className="border border-slate-300 px-4 py-2">Status chasing, fragmented communication, and delayed decision-making</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Vendor & Manufacturing Coordination</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Integrated timelines for manufacturers, couriers, and labs; SLAs; documentation; escalation paths</td>
                            <td className="border border-slate-300 px-4 py-2">No native vendor timelines, shipping coordination, or manufacturing status</td>
                            <td className="border border-slate-300 px-4 py-2">Missed windows; delays; manual email/PDF reconciliation; no clear ownership</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Chain of Identity & Chain of Custody</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Structured CoI/CoC event tracking, including timestamps, staff, and deviations</td>
                            <td className="border border-slate-300 px-4 py-2">Document uploads only; no structured CoI/CoC model</td>
                            <td className="border border-slate-300 px-4 py-2">Compliance gaps and untracked handoffs; increased audit burden</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Capacity Modeling</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Modeling for infusion chairs, ICU beds, apheresis capacity, staff competencies, and vendor slots</td>
                            <td className="border border-slate-300 px-4 py-2">Single-resource scheduling and static templates</td>
                            <td className="border border-slate-300 px-4 py-2">Preventable cancellations; under- or over-utilization; throughput constraints</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Readiness & SOP Enforcement</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Therapy-specific SOP rules; readiness gates that must be met before scheduling</td>
                            <td className="border border-slate-300 px-4 py-2">Limited clinical decision support; no multi-step readiness gating</td>
                            <td className="border border-slate-300 px-4 py-2">Infusions scheduled before readiness; increased safety and compliance risk</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Operational Documentation Management</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Versioned vendor packets, CoC forms, release certificates, and intake packages tied to milestones</td>
                            <td className="border border-slate-300 px-4 py-2">Generic, unstructured document storage</td>
                            <td className="border border-slate-300 px-4 py-2">No clear linkage of documents to workflow steps; audit inefficiency; risk of missing documents</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Automations & Alerts</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Time- and SLA-based triggers tied to vendor windows, lab timelines, and patient-specific workflows</td>
                            <td className="border border-slate-300 px-4 py-2">Mostly clinical alerts (e.g., critical lab values)</td>
                            <td className="border border-slate-300 px-4 py-2">Teams manually track critical deadlines; increased risk of delay or omission</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Longitudinal Case View</strong></td>
                            <td className="border border-slate-300 px-4 py-2">A multi-month operational timeline that combines clinical and logistical milestones</td>
                            <td className="border border-slate-300 px-4 py-2">Encounter-based views with limited operational perspective</td>
                            <td className="border border-slate-300 px-4 py-2">No forecasting; limited ability to see upcoming bottlenecks or dependencies</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-4 py-2 font-medium"><strong>Audit Traceability</strong></td>
                            <td className="border border-slate-300 px-4 py-2">Workflow-level logs mapped directly to CGT steps and SOPs</td>
                            <td className="border border-slate-300 px-4 py-2">Clinical access and activity logs only</td>
                            <td className="border border-slate-300 px-4 py-2">Manual reconstruction of operational history; audit stress and potential gaps</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.1 Multi‑Step Workflow Orchestration</h3>
                    <p>
                      CGT pathways span months and involve numerous branching steps. EHR tools can configure some order sets and basic workflows, but they cannot orchestrate 20–70 interdependent milestones that include non‑clinical tasks, vendor steps, logistics checkpoints, and formal approvals. As a result, coordination teams create sidecar systems in Excel, Smartsheet, SharePoint, or custom databases just to answer basic questions such as "What comes next?" and "Who owns it?"
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.2 Limited Cross‑Team Visibility</h3>
                    <p>
                      A typical CGT case touches hematology/oncology, BMT, apheresis, the cellular therapy lab, pharmacy, inpatient oncology, ICU, financial clearance, scheduling, and multiple external vendors. No single EHR screen provides a shared, real‑time view of where the patient is in the therapy journey, what is blocking progress, and what comes next. Staff rely on phone calls, emails, and meetings to keep everyone aligned.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.3 Lack of Manufacturing & Vendor Coordination</h3>
                    <p>
                      CGT depends on precise alignment with manufacturer slots, courier pickup windows, release testing, cryopreservation, and return shipments. EHRs do not have a native concept of vendors, SLAs, or manufacturing milestones. These details are typically scattered across email threads, vendor portals, and siloed documents—with no unified operational record.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.4 Insufficient CoI/CoC Workflow Support</h3>
                    <p>
                      While EHRs can store scanned forms and notes, they do not manage the CoI/CoC workflow itself. The sequence of who handed what to whom, when, under what conditions, and with what documentation is often recorded in paper logs or unstructured files. This exposes programs to compliance risk and makes audit preparation difficult.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.5 No Capacity‑Driven Scheduling</h3>
                    <p>
                      CGT scheduling requires aligning infusion chairs, ICU backup, apheresis capacity, pharmacy workload, staff skills, and manufacturer windows—often for multiple patients at once. EHR scheduling modules focus on booking encounters, not on optimizing capacity across services. Without a dedicated capacity model, sites experience preventable cancellations, reschedules, and suboptimal utilization of high‑value assets.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.6 Minimal SOP Enforcement & Readiness Gating</h3>
                    <p>
                      CGT protocols and standards (e.g., FACT/JACIE) mandate specific tasks and documentation before patients proceed. Most EHRs cannot enforce multi‑step readiness gating that validates all requirements prior to infusion or apheresis. Readiness is instead managed by coordinators through manual checklists and institutional knowledge.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">2.7 Clinical Logs Without Operational Traceability</h3>
                    <p>
                      EHR audit logs capture clinical activity but do not map to CGT workflows. For regulators and accreditors, sites must often reconstruct an operational story from clinical notes, emails, and external systems. This is time‑consuming and can expose documentation gaps.
                    </p>
                  </>
                )}
                {s.id === "cellcx-operational" && (
                  <>
                    <p>
                      CellCX is designed specifically to address these gaps. The EHR continues to serve as the clinical system of record, while CellCX becomes the operational nervous system for CGT workflows. Integration between the two is achieved through standards such as HL7 and FHIR, allowing data to move where it needs to without duplicating effort.
                    </p>
                    <br />
                    <p>
                      Inbound from the EHR to CellCX typically includes referrals, consults, orders, demographics, insurance data, and key clinical results. Outbound from CellCX back to the EHR includes workflow status summaries, milestone updates such as "apheresis completed" or "product received," and structured notes or documents that belong in the patient's chart. Clinicians can continue working primarily in the EHR, while operational teams use CellCX to manage the wider therapy lifecycle.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <img 
                        src="/Image/WhitePapers/Digitally Orchestrating Cell & Gene Therapy/Digitally Orchestrating Cell & Gene Therapy_Image2.png" 
                        alt="CellCX: The Operational Engine that Complements the EHR"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                  </>
                )}
                {s.id === "workflow-digitization" && (
                  <>
                    <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Referral & Intake</h3>
                    <p>
                      CellCX can automatically create a CGT case from an EHR referral or consultation. Each case is associated with an indication, therapy type, referring clinician, and site. The platform supports triage queues, referral completeness scoring, and tracking of financial authorization and payer requirements. Multidisciplinary review workflows can be configured to ensure that complex cases receive appropriate oversight before proceeding.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Pre‑Apheresis Workup</h3>
                    <p>
                      In the pre‑apheresis phase, CellCX enforces baseline lab requirements, cardiac and respiratory clearance, infectious disease screening, imaging, vendor enrollment, patient education, and consent. Each of these tasks can be defined as a workflow step with ownership, due dates, and escalation rules. Readiness gates prevent accidental scheduling of apheresis before all required steps have been completed.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">4.3 Apheresis Scheduling</h3>
                    <p>
                      Apheresis is a critical milestone that must align with manufacturer availability and shipping logistics. CellCX can model multi‑resource availability—including chairs, machines, staff, and rooms—and coordinate with vendor slots. Pre‑collection lab validation and courier pickup planning can be built into the workflow, reducing the risk of last‑minute delays or cancellations.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">4.4 Manufacturing & Logistics</h3>
                    <p>
                      During manufacturing, CellCX tracks CoI and CoC events, shipment pickups and deliveries, cryopreservation and storage status, and vendor milestones such as "manufacturing started," "product ready for release," and "product shipped." Vendor documentation can be stored and version‑controlled within the workflow, and time‑based alerts can notify teams of delays or impending deadlines tied to service‑level agreements.
                    </p>
                    <br />
                    <h3 className="text-xl font-semibold mt-6 mb-3">4.5 Infusion Planning & Readiness</h3>
                    <p>
                      For infusion, CellCX supports capacity‑based scheduling across infusion chairs, beds, ICU contingency, and pharmacy workload. Readiness dashboards use a traffic‑light model (e.g., green/yellow/red) to indicate whether requirements have been met.
                    </p>
                  </>
                )}
                {s.id === "strategic-benefits" && (
                  <>
                    <p>
                      By adding CellCX as an operational layer alongside the EHR, organizations can achieve measurable improvements in efficiency, throughput, compliance, and financial performance:
                    </p>
                    <br />
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Operational efficiency</strong> – Consolidate spreadsheets, whiteboards, email chains, and ad‑hoc trackers into one workflow system. Coordination teams can manage more cases per FTE, with less time spent chasing status.</li>
                      <li><strong>Throughput and capacity</strong> – Improve utilization of infusion chairs, apheresis machines, ICU backup, and skilled staff. Reduce preventable cancellations and reschedules caused by readiness issues.</li>
                      <li><strong>Compliance and audit readiness</strong> – Capture structured, time‑stamped logs for every operational step, aligned with FACT, NMDP, and FDA expectations. Reduce the time and stress of audit preparation.</li>
                      <li><strong>Financial performance</strong> – At per‑patient revenues of $400K–$700K for many autologous therapies, each avoided cancellation or delay has a significant financial impact. CellCX helps protect and grow CGT revenue by supporting smoother, more predictable operations.</li>
                    </ul>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <img 
                        src="/Image/WhitePapers/Digitally Orchestrating Cell & Gene Therapy/Digitally Orchestrating Cell & Gene Therapy_Image3.png" 
                        alt="Strategic Benefits of CellCX for CGT Programs"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol className="list-decimal pl-6">
                      <li>
                        <strong>IQVIA (2023). Decisions in Cell and Gene Therapy Development.</strong>
                        <br />
                        Industry analysis on CGT market scale, pipeline evolution, and projected CAGR.
                        <br />
                        Note: IQVIA pages may require cookie/region acceptance before loading.
                        <br />
                        Source: IQVIA Institute for Human Data Science.
                      </li>
                      <li>
                        <strong>Grand View Research (2024). Gene Therapy Market Size Report.</strong>
                        <br />
                        Market sizing for gene therapy products through 2030, including segment-level CAGR.
                      </li>
                      <li>
                        <strong>Grand View Research (2024). Cell Therapy Market Size Report.</strong>
                        <br />
                        Market sizing and forecast for global cell therapy markets, including CAR-T expansion.
                      </li>
                      <li>
                        <strong>Alliance for Regenerative Medicine (ARM) (2024). State of the Industry Report.</strong>
                        <br />
                        Sector overview including trial activity, financing trends, regulatory milestones, and commercialization progress. Note: ARM publications may require cookie consent or may open through a PDF viewer.
                      </li>
                      <li>
                        <strong>U.S. Food and Drug Administration (FDA). Approved Cellular and Gene Therapy Products.</strong>
                        <br />
                        Official list of approved CGT products, regulatory pathways, and safety considerations.
                      </li>
                      <li>
                        <strong>McKinsey & Company. Cell and Gene Therapy Insights (multiple publications).</strong>
                        <br />
                        Strategic analyses on CGT commercialization, operational readiness, manufacturing scale-up, and market outlook. Note: McKinsey content often requires region and cookie acceptance.
                      </li>
                    </ol>
                  </>
                )}
                {s.id === "about" && (
                  <>
                    <p>
                      Eceptionist has been delivering secure, cloud‑based workflow solutions for more than two decades, supporting healthcare organizations around the world. The EceptionistCX platform provides modular capabilities for referrals, scheduling, telehealth, eConsults, and complex pathway management.
                    </p>
                    <br />
                    <p>
                      CellCX is Eceptionist's dedicated module for cell and gene therapy operations. It is designed to complement major EHR systems by providing the operational infrastructure required to scale CGT programs safely, efficiently, and compliantly.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how CellCX provides the operational layer that modern EHR systems cannot deliver for cell and gene therapy programs, addressing complex workflows, vendor coordination, and compliance requirements.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>$20-25B global CGT market in 2023</li>
                <li>2,000+ active clinical trials</li>
                <li>30-70 operational steps per patient</li>
                <li>EHR limitations in CGT operations</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "market-context" && "Market Context: A Decade of Unprecedented CGT Expansion"}
                      {s.id === "operational-gaps" && "Operational Gaps in Major EHR Platforms"}
                      {s.id === "cellcx-operational" && "CellCX: The Operational Engine"}
                      {s.id === "workflow-digitization" && "End‑to‑End Workflow Digitization"}
                      {s.id === "strategic-benefits" && "Strategic Benefits of CellCX"}
                      {s.id === "references" && "References"}
                      {s.id === "about" && "About Eceptionist and CellCX"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX | Collaborative Healthcare Platform. "Digitally Orchestrating Cell & Gene Therapy: Why CellCX Provides the Operational Layer Modern EHR Systems Cannot." December 4, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX | Collaborative Healthcare Platform. "Digitally Orchestrating Cell & Gene Therapy: Why CellCX Provides the Operational Layer Modern EHR Systems Cannot." December 4, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "Scaling Cell & Gene Therapy Programs", description: "How technology enables safe, efficient, and compliant workflows for CGT programs.", route: "/whitepapers/cell-cx" },
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs across healthcare systems.", route: "/whitepapers/integrated-care" },
              { title: "AI in Healthcare Operations", description: "Practical applications for smarter collaboration and better care delivery.", route: "/whitepapers/ai-in-healthcare-operations" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Digitally Orchestrating Cell & Gene Therapy: Why CellCX Provides the Operational Layer Modern EHR Systems Cannot"
        pdfPath="/PDF/WhitePapers/CellCX_EHR_WhitePaper v2.pdf"
      />
    </div>
  )
}

export default DigitallyOrchestratingCellGeneTherapy
